import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { differenceInMinutes, parseISO } from "date-fns";

import { type ShiftType } from "../api/usePaginatedWorkerShifts";
import { ShiftHistoryCardContent } from "./CardContent";

interface ShiftHistoryCardProps {
  shift: ShiftType;
  workplaceInfo?: {
    name?: string;
    timezone?: string;
  };
  totalShiftTimeInHours: number;
  shouldDisableButton: boolean;
  isMissedPunchRequestEnabled: boolean;
  hasPendingExtraWorkedTimeRequest: boolean;
  isTimesheetUploaded: boolean;
  isRequestingShiftAdjustment: boolean;
  onUploadTimesheetClick: () => void;
  onChatWithSupportClick: () => void;
  onRequestShiftAdjustmentClick: () => void;
}

export function ShiftHistoryCard(props: ShiftHistoryCardProps) {
  const {
    shift,
    workplaceInfo,
    totalShiftTimeInHours,
    shouldDisableButton,
    isMissedPunchRequestEnabled,
    hasPendingExtraWorkedTimeRequest,
    isTimesheetUploaded,
    isRequestingShiftAdjustment,
    onUploadTimesheetClick,
    onChatWithSupportClick,
    onRequestShiftAdjustmentClick,
  } = props;

  const workplaceId = shift.relationships?.workplace?.data?.id;
  const isVerified = shift.attributes?.verified ?? false;

  const qualificationName = shift.attributes?.requirements?.qualificationName ?? "";
  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    qualificationName,
  ]);

  const { mandatoryBreakDurationInMinutes: workplaceBreakDurationInMinutes } =
    useGetFacilityBreakConfiguration({
      workplaceId: isVerified ? undefined : workplaceId,
    });

  const qualificationText =
    qualificationNameToDisplayLabelMap.get(qualificationName) ?? qualificationName;

  if (!isDefined(shift) || !isDefined(workplaceId)) {
    return null;
  }

  const shiftClockInTime =
    shift.attributes?.clockInOut?.start ?? shift.attributes?.schedule?.startAt;
  const shiftClockOutTime = shift.attributes?.clockInOut?.end ?? shift.attributes?.schedule?.endAt;

  if (!isDefined(shiftClockInTime) || !isDefined(shiftClockOutTime)) {
    return null;
  }

  if (!isDefined(workplaceInfo?.name)) {
    return null;
  }

  // pay.total is the hourly pay rate for the shift
  // totalShiftTimeInHours includes shift time and approved break time
  const totalPayInDollars = totalShiftTimeInHours * (shift.attributes?.pay?.total ?? 0);
  const timeSlot = shift.attributes?.schedule?.timeSlot;

  // For verified shifts, calculate break duration from actual lunchInOut times
  const lunchInOutStart = shift.attributes?.lunchInOut?.start;
  const lunchInOutEnd = shift.attributes?.lunchInOut?.end;
  const verifiedBreakDurationInMinutes =
    isDefined(lunchInOutStart) && isDefined(lunchInOutEnd)
      ? differenceInMinutes(parseISO(lunchInOutEnd), parseISO(lunchInOutStart))
      : 0;

  return (
    <Card
      key={shift.id}
      outlined
      sx={{
        flexGrow: 1,
        marginBottom: 6,
      }}
      variant="quaternary"
    >
      <ShiftHistoryCardContent
        timeSlot={shiftPeriodToTimeSlot[timeSlot as ShiftPeriod]}
        workplaceId={workplaceId}
        workplaceName={workplaceInfo?.name ?? ""}
        workplaceTimezone={workplaceInfo?.timezone ?? DEFAULT_TIMEZONE}
        shiftStartDate={parseISO(shiftClockInTime)}
        shiftEndDate={parseISO(shiftClockOutTime)}
        totalPayInDollars={totalPayInDollars}
        qualificationText={qualificationText}
        isVerified={isVerified}
        verifiedBreakDurationInMinutes={verifiedBreakDurationInMinutes}
        workplaceBreakDurationInMinutes={workplaceBreakDurationInMinutes}
        shouldDisableButton={shouldDisableButton}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
        hasPendingExtraWorkedTimeRequest={hasPendingExtraWorkedTimeRequest}
        isTimesheetUploaded={isTimesheetUploaded}
        isRequestingShiftAdjustment={isRequestingShiftAdjustment}
        onUploadTimesheetClick={onUploadTimesheetClick}
        onChatWithSupportClick={onChatWithSupportClick}
        onRequestShiftAdjustmentClick={onRequestShiftAdjustmentClick}
      />
    </Card>
  );
}
